import "@wmf/groove/app.css";
import "../grooveAdapter.css";
import { Notation, grooveToAbc, parseUrl } from "@wmf/groove";
import { grooveSnapshotHref } from "../grooves.js";
import { SetAssetPracticeTask } from "../SetAssetPracticeTask.js";
import type { AssetPracticeTarget } from "@wmf/shared/assignedAssetPractice";

/** A groove attached to an earlier lesson, as the snapshot sent with it. */
export interface PreviousLessonGroove {
  id: string;
  title: string;
  /** Opens the snapshot attached to that lesson. */
  token: string;
  /** The lesson's frozen pattern. Older responses may omit it. */
  query?: string;
  /** Null until a browser has drawn and uploaded one. */
  renderUrl: string | null;
}

/**
 * The music a previous lesson left behind, for private and School lessons
 * alike. Each link opens the lesson's saved snapshot in a new tab, so the
 * write-up in progress is left where it was.
 */
export function PreviousLessonGrooveLinks({
  grooves,
  headingId,
  practiceTarget,
  noteId,
  onPracticeChanged,
}: {
  grooves: readonly PreviousLessonGroove[];
  headingId: string;
  practiceTarget?: AssetPracticeTarget;
  noteId?: string;
  onPracticeChanged?: () => void;
}) {
  if (grooves.length === 0) return null;

  return (
    <section className="lesson-tool-previous-attachments" aria-labelledby={headingId}>
      <h3 id={headingId}>Attached music</h3>
      {grooves.map((groove) => (
        <div className="lesson-tool-groove-attachment" key={groove.id}>
          <a href={grooveSnapshotHref(groove.token)} target="_blank" rel="noreferrer">
            Open {groove.title}
          </a>
          {practiceTarget && noteId && <SetAssetPracticeTask target={practiceTarget} source={{ kind: "lesson_groove", noteId, id: groove.id }}
            assetTitle={groove.title} onSaved={onPracticeChanged} />}
          {groove.renderUrl ? (
            <img src={groove.renderUrl} alt={`Notation for ${groove.title}`} />
          ) : typeof groove.query === "string" ? (
            <SnapshotNotation query={groove.query} />
          ) : (
            <span className="small muted">Notation image not available.</span>
          )}
        </div>
      ))}
    </section>
  );
}

/** Draw the attached pattern without reading or uploading the library groove. */
function SnapshotNotation({ query }: { query: string }) {
  const groove = parseUrl(query);
  return (
    <div className="groove-scope">
      <Notation abc={grooveToAbc(groove)} single={groove.measures === 1} />
    </div>
  );
}
